import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AssessmentsHeaderProps } from 'components/AssessmentsHeader/types';
import { useNetworkStatus } from 'hooks/useNetworkStatus';
import { selectAssessmentUserFirstName } from '../assessmentsAuthSlice';
import { selectSkippedQuestionNumbers } from '../assessmentsSlice';
import {
  generateQuestionProps,
  getAssessmentsAnsweredStateForPage,
} from '../utils/assessmentsQuestionUtils';
import { useAssessmentsAnswers } from './useAssessmentsAnswers';
import { useAssessmentsContext } from './useAssessmentsContext';
import { useAssessmentsNetworkStatus } from './useAssessmentsNetworkStaus';
import { useAssessmentsPages } from './useAssessmentsPages';
import { useAssessmentsQuestions } from './useAssessmentsQuestions';
import { useAssessmentsSkipLogic } from './useAssessmentsSkipLogic';
import { useAssessmentsCloseRequested } from './useAssessmentsCloseRequested';
import { TriggerSkipLogic } from '../types';

export const useAssessments = () => {
  const {
    formMethods: { watch, setValue, getValues, handleSubmit },
    pendingScrollTarget,
  } = useAssessmentsContext();

  const { clearRefs, triggerSkipLogic, scrollToTop } = useAssessmentsSkipLogic();
  const { paginatedQuestions, pages, setPage, currentPage } = useAssessmentsPages();
  const { questions: allQuestions } = useAssessmentsQuestions();
  const firstName = useSelector(selectAssessmentUserFirstName);
  const skippedQuestionNumbers = useSelector(selectSkippedQuestionNumbers);
  const { isOnline } = useNetworkStatus();
  useAssessmentsNetworkStatus();

  const formValues = watch();

  const skippedQuestionNumbersSet = useMemo(
    () => new Set(skippedQuestionNumbers),
    [skippedQuestionNumbers],
  );

  const clearSkippedQuestionAnswer = useCallback(
    (questionId: number) => {
      const fieldName = questionId.toString();
      const currentValue = getValues(fieldName);
      const hasValue = Array.isArray(currentValue)
        ? currentValue.length > 0
        : Boolean(currentValue);

      if (hasValue) {
        setValue(fieldName, undefined);
      }
    },
    [getValues, setValue],
  );

  const clearSkippedQuestionAnswers = useCallback(() => {
    if (!allQuestions?.length || skippedQuestionNumbersSet.size === 0) {
      return;
    }

    allQuestions.forEach((question) => {
      if (!skippedQuestionNumbersSet.has(question.questionNumber)) {
        return;
      }

      clearSkippedQuestionAnswer(question.id);
    });
  }, [allQuestions, skippedQuestionNumbersSet, clearSkippedQuestionAnswer]);

  const triggerSkipLogicWithClearing = useCallback<TriggerSkipLogic>(
    (props) => {
      triggerSkipLogic(props);
      clearSkippedQuestionAnswers();
    },
    [triggerSkipLogic, clearSkippedQuestionAnswers],
  );

  const {
    saveAndCloseAnswersOnTimeout,
    saveAndCloseAnswers,
    validateAnswers,
    submitAnswers,
    autoSaved,
    submittingAnswers,
    savingAnswers,
  } = useAssessmentsAnswers();

  useAssessmentsCloseRequested((eventId) => saveAndCloseAnswers(getValues(), eventId));

  const onPageClick = useCallback(
    (newPage: number) => {
      clearRefs();
      clearSkippedQuestionAnswers();
      const { firstUnanswered, atleastOneAnswered, firstUnskipped } =
        getAssessmentsAnsweredStateForPage(
          allQuestions ?? [],
          formValues,
          skippedQuestionNumbers,
          newPage,
        );

      setPage(newPage);
      // If the user has already answered some questions on this page,
      // scroll to the first question they haven't answered yet.
      // This works the same way for both the initial load and page changes.
      if (atleastOneAnswered && firstUnanswered) {
        pendingScrollTarget.current = firstUnanswered.id;
        return;
      }
      // Scroll to the first question that isn't skipped, but only on
      // pages after the first. On other pages the start of the page and the
      // start of the first question are the same spot, so no scroll is needed.
      if (newPage !== 1 && firstUnskipped) {
        pendingScrollTarget.current = firstUnskipped.id;
        return;
      }
      // On page 1, if there are no unanswered or unskipped questions to
      // scroll to, just scroll to the top of the assessment container.
      if (newPage === 1) {
        scrollToTop();
        return;
      }
      pendingScrollTarget.current = null;
    },
    [
      clearRefs,
      clearSkippedQuestionAnswers,
      setPage,
      allQuestions,
      formValues,
      skippedQuestionNumbers,
      pendingScrollTarget,
      scrollToTop,
    ],
  );

  const hasAnsweredQuestion = useMemo(
    () =>
      allQuestions?.some((question) => {
        const answerValue = formValues[question.id.toString()];
        const isAnswerable = [
          'checkbox',
          'textbox',
          'textarea',
          'radio',
          'date',
          'select',
        ].includes(question.type);
        const hasValue = Array.isArray(answerValue) ? answerValue.length > 0 : Boolean(answerValue);
        return isAnswerable && hasValue;
      }),
    [allQuestions, formValues],
  );

  const headerProps: AssessmentsHeaderProps = useMemo(
    () => ({
      currentPage,
      pages,
      autoSaveStatus: autoSaved ? 'saved' : 'idle',
      isSaving: savingAnswers,
      isSubmitting: submittingAnswers,
      onPageClick,
      onSaveAndClose: () => saveAndCloseAnswers(formValues),
      onSaveAndCloseOnTimeout: () => saveAndCloseAnswersOnTimeout(formValues),
      onSubmit: handleSubmit(validateAnswers),
      hasAnsweredQuestion,
      isOnline,
    }),
    [
      currentPage,
      pages,
      autoSaved,
      savingAnswers,
      submittingAnswers,
      onPageClick,
      saveAndCloseAnswers,
      saveAndCloseAnswersOnTimeout,
      handleSubmit,
      validateAnswers,
      formValues,
      hasAnsweredQuestion,
      isOnline,
    ],
  );

  const questions = useMemo(
    () =>
      paginatedQuestions?.map((question) =>
        generateQuestionProps({
          question,
          fieldValue: formValues[question.id.toString()],
          setValue,
          formValues,
          isSubQuestion: false,
          triggerSkipLogic: triggerSkipLogicWithClearing,
        }),
      ),
    [paginatedQuestions, formValues, setValue, triggerSkipLogicWithClearing],
  );

  return {
    headerProps,
    questions,
    triggerSkipLogic: triggerSkipLogicWithClearing,
    formValues,
    submitAnswers: handleSubmit(submitAnswers),
    hasAnsweredQuestion,
    currentPage,
    pagesCount: pages.length,
    firstName,
  };
};
